import { GetServerSidePropsContext } from 'next';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect } from 'react';

import {
  PAGE_TYPE,
  Referrer,
} from '@invideoio/web-shared/utils/utils.constant';

import VideoWallCategoryTemplateHead from '@components/video-wall-category-templates/VideoWallCategoryTemplateHead';
import VideoWallCategoryTemplates from '@components/video-wall-category-templates/VideoWallCategoryTemplates';
import HomepageSEO from '@components/common/SEO/HomepageSEO';
import HeaderWithCTA from '@components/header-ai';
import { footerExploreLinks } from '@constants/footerExplore';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import Footer from '@invideoio/web-shared/components/Footer';
import {
  TemplateSearchData,
  TemplateSearchQuery,
  TemplateSearchResponse,
} from '@invideoio/web-shared/types/Templates';
import { AnalyticsUtils } from '@invideoio/web-shared/utils/analytics/AnalyticsUtils';
import AppLayout from '@layouts/AppLayout';
import { fetchAllVideoWallTemplateCategories } from '@lib/API/template.api';
import { StateContext } from '@store/store';
import { AmplitudeEvents, sendAmplitudeAnalyticEvent } from '@utils/analytics';
import { BrowserType } from '@invideoio/web-shared/constants/common';
import { fetchAndEnhanceAllVideoTemplateData } from '@utils/common.utils';
import VideoWallTrendsTray from '@components/video-wall-category-templates/VideoWallTrendsTray';
import { getTrends } from '@lib/ProAPI/pro.api';
import { TrendGroup } from '@lib/ProAPI/pro-api.types';

interface TemplateCategoryPageProps {
  templateList: TemplateSearchResponse;
  filters?: any;
  templatePageData: any;
  templateCategories: any[];
  currentPage: number;
  browser: BrowserType;
  trendGroups: TrendGroup[];
}

function VideoWallTemplateCategory(props: TemplateCategoryPageProps) {
  const { filters, templatePageData, currentPage, trendGroups } = props;

  const globalState = useContext(StateContext);
  const { t, lang } = useTranslation('home');
  const { platform, os, config } = globalState;

  useEffect(() => {
    const AppName = process.env.NEXT_PUBLIC_APP_NAME;

    if (AppName === 'kizoa') {
      AnalyticsUtils.setReferrer(Referrer.KIZOA);
    } else {
      AnalyticsUtils.setReferrer(Referrer.WEBSITE);
    }

    AnalyticsUtils.registerPage(PAGE_TYPE.VIDEOWALL_CATEGORY as any, {
      location: window.location.href,
    });

    sendAmplitudeAnalyticEvent(AmplitudeEvents.appLoad);
  }, []);

  return (
    <AppLayout platform={platform}>
      <HomepageSEO seo={templatePageData.SEO} />
      <HeaderWithCTA isCTAIntersected={true} />

      <VideoWallCategoryTemplateHead Head={templatePageData.Head} />

      {trendGroups.length > 0 && (
        <VideoWallTrendsTray platform={platform} trendGroups={trendGroups} />
      )}

      <div
        className={ivclass(
          'iv-relative iv-overflow-x-hidden',
          'iv-flex md:iv-flex-col sm:iv-flex-col',
          'iv-px-40 sm:iv-px-25',
        )}
      >
        {/* <VideoWallCategoryTemplateLeftPanel
          templateCategories={templateCategories}
          templateCategoryName={templatePageData.CategoryName}
        /> */}

        <VideoWallCategoryTemplates
          templateList={templatePageData.videoWallTemplate}
          filters={filters}
          totalCount={templatePageData.videoWallTemplate.length}
          currerntPage={currentPage}
          platform={platform}
          moreLink={templatePageData.TemplateSearchFilters}
        />
      </div>

      <Footer
        os={os}
        platform={platform}
        socialLinks={globalState.socialLinks}
        exploreLinks={footerExploreLinks[config?.env?.siteId][lang].links}
      />
    </AppLayout>
  );
}

export default VideoWallTemplateCategory;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const query = context.query;

  const templatePageData = {
    SEO: {
      Title: 'Made with love, using Invideo',
      Description: '',
      CanonicalURL: 'https://invideo.io/ai-videos/',
      Hreflang: [],
    },
    Head: {
      Title: 'Made with love, using invideo AI',
      HighlightedTitle: '',
      SubTitle: 'Showcasing our user-stories, one video at a time.',
    },
    videoWallTemplate: [] as Array<any>,
  };

  let page: number | string | string[] | undefined = query.page;

  if (typeof page === 'string') {
    page = parseInt(page);
  } else {
    page = 1;
  }

  const templateCategories = await fetchAllVideoWallTemplateCategories();

  let templateList: TemplateSearchData | null = null;

  let filters;
  filters = {} as TemplateSearchQuery;
  filters.search_term = 'ai';

  if (templatePageData.videoWallTemplate) {
    await fetchAndEnhanceAllVideoTemplateData(templatePageData);
  }

  if (templatePageData.videoWallTemplate?.length === 0) {
    return {
      notFound: true,
    };
  }

  const trendsResponse = await getTrends();

  const trendGroups = trendsResponse?.trends || [];

  return {
    props: {
      templatePageData,
      templateList,
      filters,
      templateCategories,
      currentPage: page,
      trendGroups,
    },
  };
}
